import { type FC, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useTheme } from "../../context/ThemeContext";
import { getChartTheme } from "../../theme/chartTheme";
import {
  TIMING_KIND_LABELS,
  type TimingEntry,
  type TimingKind,
} from "../../types/timings";
import { formatDurationMs } from "../../utils/time";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

export interface TimingsChartsProps {
  entries: TimingEntry[];
}

/* ------------------------------------------------------------------ */
/*  Data helpers                                                       */
/* ------------------------------------------------------------------ */

interface HistogramBucket {
  label: string;
  count: number;
}

interface HistogramResult {
  buckets: HistogramBucket[];
  allZero: boolean;
}

function buildHistogram(entries: TimingEntry[]): HistogramResult | null {
  const durations = entries
    .map((entry) => entry.duration_ms)
    .filter((value) => Number.isFinite(value));
  if (durations.length === 0) {
    return null;
  }

  const bucketCount = 5;
  const max = Math.max(0, ...durations);
  if (max === 0) {
    return {
      allZero: true,
      buckets: [{ count: durations.length, label: formatDurationMs(0) }],
    };
  }

  const positives = durations.filter((d) => d > 0);
  const minPositive = positives.length > 0 ? Math.min(...positives) : 1;
  const useLogBuckets = minPositive > 0 && max / minPositive > 50;

  const boundaries: number[] = [];
  if (useLogBuckets) {
    const logMin = Math.log10(Math.max(1, minPositive * 0.9));
    const logMax = Math.log10(max);
    const logStep = (logMax - logMin) / bucketCount;
    boundaries.push(0);
    for (let i = 1; i < bucketCount; i++) {
      boundaries.push(10 ** (logMin + i * logStep));
    }
    boundaries.push(max);
  } else {
    const bucketSize = max / bucketCount;
    for (let i = 0; i <= bucketCount; i++) {
      boundaries.push(i === bucketCount ? max : i * bucketSize);
    }
  }

  const buckets: HistogramBucket[] = Array.from(
    { length: bucketCount },
    (_, index) => ({
      count: 0,
      label: `${formatDurationMs(boundaries[index])} – ${formatDurationMs(boundaries[index + 1])}`,
    }),
  );

  for (const duration of durations) {
    let index = bucketCount - 1;
    for (let i = 1; i < boundaries.length; i++) {
      if (duration < boundaries[i]) {
        index = i - 1;
        break;
      }
    }
    buckets[index].count += 1;
  }

  return { allZero: false, buckets };
}

interface KindBucket {
  kind: TimingKind;
  label: string;
  count: number;
}

function buildTopKinds(entries: TimingEntry[]): KindBucket[] {
  const counts = new Map<TimingKind, number>();
  for (const entry of entries) {
    counts.set(entry.kind, (counts.get(entry.kind) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([kind, count]) => ({ count, kind, label: TIMING_KIND_LABELS[kind] }))
    .toSorted((a, b) => b.count - a.count)
    .slice(0, 5);
}

/* ------------------------------------------------------------------ */
/*  Theme-aware chart colors                                           */
/* ------------------------------------------------------------------ */

function useChartColors() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Shared chrome (tooltip surface, axis, grid) comes from getChartTheme so all
  // charts stay consistent; the fills below are this chart family's own series
  // colors. (Previously this hook carried a divergent neutral tooltipBorder —
  // now unified to the shared emerald-tinted border.)
  const base = getChartTheme(isDark);
  return {
    axisText: base.axisText,
    cursorFill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    gridLine: base.gridLine,
    histogramFill: isDark
      ? "rgba(56, 189, 248, 0.60)"
      : "rgba(14, 165, 233, 0.70)",
    kindFill: isDark ? "rgba(52, 211, 153, 0.60)" : "rgba(16, 185, 129, 0.70)",
    tooltipBg: base.tooltipBg,
    tooltipBorder: base.tooltipBorder,
    tooltipLabel: base.tooltipMuted,
    tooltipText: base.tooltipText,
  };
}

/* ------------------------------------------------------------------ */
/*  Custom tooltip                                                     */
/* ------------------------------------------------------------------ */

interface ChartTooltipPayloadEntry {
  value: number;
  payload: { label?: string; kind?: string };
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayloadEntry[];
  colors: ReturnType<typeof useChartColors>;
  valueLabel: string;
}

const ChartTooltip: FC<ChartTooltipProps> = ({
  active,
  payload,
  colors,
  valueLabel,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const [entry] = payload;

  return (
    <div
      className="rounded-none border px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest shadow-sm"
      style={{
        backgroundColor: colors.tooltipBg,
        border: `1px solid ${colors.tooltipBorder}`,
        color: colors.tooltipText,
      }}
    >
      <div style={{ color: colors.tooltipLabel }}>
        {entry.payload.label ?? entry.payload.kind ?? ""}
      </div>
      <div className="font-semibold">
        {entry.value.toLocaleString()} {valueLabel}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared axis / grid style                                           */
/* ------------------------------------------------------------------ */

const AXIS_TICK_STYLE = { fontSize: 10 };

const HistogramChart: FC<{
  buckets: HistogramBucket[];
  colors: ReturnType<typeof useChartColors>;
  entryCount: number;
}> = ({ buckets, colors, entryCount }) => (
  <ResponsiveContainer width="100%" height={160}>
    <BarChart
      data={buckets}
      margin={{ bottom: 0, left: -16, right: 4, top: 4 }}
    >
      <CartesianGrid vertical={false} stroke={colors.gridLine} />
      <XAxis
        dataKey="label"
        tick={AXIS_TICK_STYLE}
        stroke={colors.axisText}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        allowDecimals={false}
        tick={AXIS_TICK_STYLE}
        stroke={colors.axisText}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip
        cursor={{ fill: colors.cursorFill }}
        content={
          <ChartTooltip
            colors={colors}
            valueLabel={entryCount === 1 ? "entry" : "entries"}
          />
        }
      />
      <Bar
        dataKey="count"
        radius={[4, 4, 0, 0]}
        maxBarSize={48}
        fill={colors.histogramFill}
      />
    </BarChart>
  </ResponsiveContainer>
);

const TopKindsChart: FC<{
  colors: ReturnType<typeof useChartColors>;
  entriesCount: number;
  topKinds: KindBucket[];
}> = ({ colors, entriesCount, topKinds }) => (
  <ResponsiveContainer width="100%" height={160}>
    <BarChart
      data={topKinds}
      layout="vertical"
      margin={{ bottom: 0, left: 0, right: 4, top: 4 }}
    >
      <CartesianGrid horizontal={false} stroke={colors.gridLine} />
      <XAxis
        type="number"
        allowDecimals={false}
        tick={AXIS_TICK_STYLE}
        stroke={colors.axisText}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        type="category"
        dataKey="label"
        width={72}
        tick={AXIS_TICK_STYLE}
        stroke={colors.axisText}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip
        cursor={{ fill: colors.cursorFill }}
        content={
          <ChartTooltip
            colors={colors}
            valueLabel={entriesCount === 1 ? "entry" : "entries"}
          />
        }
      />
      <Bar
        dataKey="count"
        radius={[0, 4, 4, 0]}
        maxBarSize={20}
        fill={colors.kindFill}
      />
    </BarChart>
  </ResponsiveContainer>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const TimingsCharts: FC<TimingsChartsProps> = ({ entries }) => {
  const histogram = useMemo(() => buildHistogram(entries), [entries]);
  const topKinds = useMemo(() => buildTopKinds(entries), [entries]);
  const colors = useChartColors();

  if (entries.length === 0) {
    return (
      <div className="space-y-3">
        <SectionHeader title="Charts" />
        <div className="rounded-none border border-slate-300 bg-white p-4 text-sm text-gray-500 dark:border-slate-700 dark:bg-arti-panel dark:text-white/60">
          No timing entries available to chart yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader title="Charts" />
      <div className="grid gap-3 lg:grid-cols-2">
        {/* Duration distribution histogram */}
        <ContentCard className="space-y-3">
          <Text variant="label" as="div">
            Duration distribution
          </Text>
          {!histogram && (
            <div className="text-sm text-gray-500 dark:text-white/60">
              No duration data available.
            </div>
          )}
          {histogram?.allZero && (
            <div className="text-sm text-gray-500 dark:text-white/60">
              All entries have zero duration.
            </div>
          )}
          {histogram && !histogram.allZero && (
            <div>
              <HistogramChart
                buckets={histogram.buckets}
                colors={colors}
                entryCount={entries.length}
              />
              <Text as="div" className="mt-1">
                {entries.length.toLocaleString()} entries in view
              </Text>
            </div>
          )}
        </ContentCard>

        {/* Top kinds horizontal bar */}
        <ContentCard className="space-y-3">
          <Text variant="label" as="div">
            Top kinds
          </Text>
          {topKinds.length > 0 ? (
            <TopKindsChart
              colors={colors}
              entriesCount={entries.length}
              topKinds={topKinds}
            />
          ) : (
            <div className="text-sm text-gray-500 dark:text-white/60">
              No kinds available for the current selection.
            </div>
          )}
        </ContentCard>
      </div>
    </div>
  );
};
